import { AlertCircle, Clock, Edit3, GitBranch, Loader2, Pause, Play, Plus, Search, Trash2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FlowCreateModal } from "@/components/flows/FlowCreateModal";
import { PageHeader, useToast } from "@/components/shared";
import { flowService } from "@/lib/services";
import type { Flow, FlowRunSummary, FlowSummary } from "@/types";

export function AgentFlows() {
  const navigate = useNavigate();
  const [flows, setFlows] = useState<FlowSummary[]>([]);
  const [flowPausedRuns, setFlowPausedRuns] = useState<Map<string, number>>(new Map());
  const [flowLatestFailure, setFlowLatestFailure] = useState<Map<string, FlowRunSummary>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const { showToast } = useToast();
  const [editingFlow, setEditingFlow] = useState<Flow | null>(null);
  const hasFetched = useRef(false);

  useEffect(() => {
    if (!hasFetched.current) {
      hasFetched.current = true;
      loadFlows();
    }
    const interval = setInterval(checkPausedRuns, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadFlows = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await flowService.list({ pageSize: 100 });
      setFlows(response.data);
      await checkPausedRuns(response.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load flows");
      console.error("Error loading flows:", err);
    } finally {
      setLoading(false);
    }
  };

  const checkPausedRuns = async (flowList?: FlowSummary[]) => {
    const flowsToCheck = flowList || flows;
    const pausedCounts = new Map<string, number>();
    const latestFailures = new Map<string, FlowRunSummary>();

    await Promise.all(
      flowsToCheck.map(async (flow) => {
        try {
          const runsResponse = await flowService.getRuns(flow.uuid, { page: 1, pageSize: 10 });
          const pausedCount = runsResponse.data.filter((run) => run.status === "paused").length;
          if (pausedCount > 0) {
            pausedCounts.set(flow.uuid, pausedCount);
          }
          const mostRecentRun = runsResponse.data[0];
          if (mostRecentRun?.status === "failed") {
            latestFailures.set(flow.uuid, mostRecentRun);
          }
        } catch (err) {
          console.error(`Error checking runs for flow ${flow.uuid}:`, err);
        }
      }),
    );

    setFlowPausedRuns(pausedCounts);
    setFlowLatestFailure(latestFailures);
  };

  const handleDeleteFlow = async (flowUuid: string) => {
    try {
      setActionLoading(flowUuid);
      await flowService.delete(flowUuid);
      setFlows((prev) => prev.filter((f) => f.uuid !== flowUuid));
    } catch (err) {
      console.error("Error deleting flow:", err);
      showToast("Failed to delete workflow", "error");
    } finally {
      setActionLoading(null);
    }
  };

  const handleCreateFlow = () => {
    setEditingFlow(null);
    setShowCreateModal(true);
  };

  const handleEditFlow = async (flowSummary: FlowSummary) => {
    try {
      setActionLoading(flowSummary.uuid);
      const response = await flowService.get(flowSummary.uuid);
      setEditingFlow(response.data);
      setShowCreateModal(true);
    } catch (err) {
      console.error("Error loading flow for edit:", err);
      showToast("Failed to load workflow details", "error");
    } finally {
      setActionLoading(null);
    }
  };

  const handleSaveFlow = async (data: {
    name: string;
    description: string;
    purpose: string;
    steps: Array<Record<string, unknown>>;
    connectors?: Array<Record<string, unknown>>;
  }) => {
    try {
      setActionLoading("creating");
      if (editingFlow) {
        await flowService.update(editingFlow.uuid, {
          name: data.name,
          description: data.description,
          purpose: data.purpose || undefined,
          steps: data.steps as never[],
          connectors: data.connectors as never[],
        });
      } else {
        await flowService.create({
          name: data.name,
          description: data.description,
          purpose: data.purpose || undefined,
          visibility: "private",
          steps: data.steps as never[],
          connectors: data.connectors as never[],
        });
      }
      await loadFlows();
      setShowCreateModal(false);
      setEditingFlow(null);
      showToast(editingFlow ? "Workflow updated" : "Workflow created", "success");
    } catch (err) {
      console.error("Error saving flow:", err);
      showToast("Failed to save workflow", "error");
    } finally {
      setActionLoading(null);
    }
  };

  const handleCancelCreate = () => {
    setShowCreateModal(false);
    setEditingFlow(null);
  };

  const filteredFlows = flows.filter(
    (flow) =>
      flow.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (flow.description?.toLowerCase() || "").includes(searchQuery.toLowerCase()),
  );

  const getStatusColor = (flow: FlowSummary) => {
    if (flow.stepCount === 0) return "bg-surface-tertiary/20 text-content-tertiary";
    if (flow.hasActiveSchedule) return "bg-green-500/20 text-green-400";
    return "bg-blue-500/20 text-blue-400";
  };

  const getStatusText = (flow: FlowSummary) => {
    if (flow.stepCount === 0) return "draft";
    if (flow.hasActiveSchedule) return "scheduled";
    return "ready";
  };

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <PageHeader
        icon={GitBranch}
        title="Workflows"
        subtitle="Build repeatable multi-agent processes to automate complex tasks"
        actions={
          <button
            onClick={handleCreateFlow}
            className="flex items-center gap-2 px-4 py-2.5 bg-brand-gradient text-white rounded-lg font-medium hover:opacity-90 transition-opacity"
          >
            <Plus className="h-4 w-4" />
            Create Workflow
          </button>
        }
      >
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-content-quaternary" />
          <input
            type="text"
            placeholder="Search workflows..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-64 pl-9 pr-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary placeholder:text-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50 text-sm"
          />
        </div>
      </PageHeader>

      {/* Flow List */}
      <div className="flex-1 overflow-auto p-6">
        {(() => {
          if (loading) {
            return (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="h-8 w-8 text-brand-primary animate-spin" />
              </div>
            );
          }
          if (error) {
            return (
              <div className="text-center py-16">
                <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-3" />
                <p className="text-red-400 mb-4">{error}</p>
                <button type="button" onClick={loadFlows} className="text-brand-primary hover:underline text-sm">
                  Retry
                </button>
              </div>
            );
          }
          if (filteredFlows.length === 0) {
            return (
              <div className="text-center py-16">
                <GitBranch className="h-12 w-12 text-content-quaternary mx-auto mb-3" />
                <p className="text-content-tertiary">
                  {searchQuery ? "No workflows match your search" : "No workflows yet"}
                </p>
              </div>
            );
          }
          return (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-7xl">
              {filteredFlows.map((flow) => (
                <FlowCard
                  key={flow.uuid}
                  flow={flow}
                  pausedCount={flowPausedRuns.get(flow.uuid)}
                  latestFailure={flowLatestFailure.get(flow.uuid)}
                  statusColor={getStatusColor(flow)}
                  statusText={getStatusText(flow)}
                  isLoading={actionLoading === flow.uuid}
                  onOpen={() => navigate(`/flows/${flow.uuid}`)}
                  onRun={() => navigate(`/flows/${flow.uuid}/run`)}
                  onEdit={() => handleEditFlow(flow)}
                  onDelete={() => handleDeleteFlow(flow.uuid)}
                />
              ))}
            </div>
          );
        })()}
      </div>

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <FlowCreateModal editingFlow={editingFlow} onSave={handleSaveFlow} onCancel={handleCancelCreate} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// FlowCard sub-component
// ---------------------------------------------------------------------------

interface FlowCardProps {
  flow: FlowSummary;
  pausedCount?: number;
  latestFailure?: FlowRunSummary;
  statusColor: string;
  statusText: string;
  isLoading: boolean;
  onOpen: () => void;
  onRun: () => void;
  onEdit: () => void;
  onDelete: () => void;
}

function FlowCard({
  flow,
  pausedCount,
  latestFailure,
  statusColor,
  statusText,
  isLoading,
  onOpen,
  onRun,
  onEdit,
  onDelete,
}: Readonly<FlowCardProps>) {
  return (
    <div className="relative bg-surface-primary border rounded-xl p-5 transition-all hover:border-brand-primary/50 border-border-primary">
      {/* Badges */}
      <div className="flex items-center gap-2 mb-3">
        {pausedCount && pausedCount > 0 && (
          <span className="flex items-center gap-1 px-2 py-0.5 bg-yellow-500/10 border border-yellow-500/30 text-yellow-400 rounded-full text-xs">
            <Pause className="h-3 w-3" />
            {pausedCount} paused
          </span>
        )}
        {latestFailure && (
          <span className="flex items-center gap-1 px-2 py-0.5 bg-red-500/10 border border-red-500/30 text-red-400 rounded-full text-xs">
            <AlertCircle className="h-3 w-3" />
            failed
          </span>
        )}
      </div>

      {/* Title + description (clickable to open detail) */}
      <button type="button" onClick={onOpen} className="text-left w-full mb-3">
        <h3 className="text-content-primary font-medium mb-1 hover:text-brand-primary transition-colors">
          {flow.name}
        </h3>
        {flow.description && <p className="text-content-tertiary text-sm line-clamp-2">{flow.description}</p>}
      </button>

      {/* Meta */}
      <div className="flex items-center gap-3 text-xs text-content-quaternary mb-4">
        <span className={`px-2 py-0.5 rounded-full font-medium ${statusColor}`}>{statusText}</span>
        <span>{flow.stepCount} steps</span>
        {flow.executionCount > 0 && <span>{flow.executionCount} runs</span>}
        {flow.hasActiveSchedule && (
          <span className="flex items-center gap-1">
            <Clock className="h-3 w-3" />
            scheduled
          </span>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2">
        <button
          onClick={onRun}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-brand-gradient text-white rounded-lg text-xs font-medium hover:opacity-90 transition-opacity"
        >
          <Play className="h-3 w-3" />
          Run
        </button>
        <button
          onClick={onEdit}
          disabled={isLoading}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-surface-secondary text-content-secondary rounded-lg text-xs hover:bg-interactive-active transition-colors disabled:opacity-50"
        >
          <Edit3 className="h-3 w-3" />
          Edit
        </button>
        <button
          onClick={onDelete}
          disabled={isLoading}
          className="flex items-center gap-1.5 px-3 py-1.5 text-content-quaternary hover:text-red-400 hover:bg-red-500/10 rounded-lg text-xs transition-colors disabled:opacity-50"
        >
          <Trash2 className="h-3 w-3" />
        </button>
      </div>
    </div>
  );
}
